import React, { useState, FormEvent } from 'react';
import useSWR from 'swr';
import api from '../services/api';

interface PlanExercise {
  name: string;
  sets: number;
  reps_range: string;
  notes?: string;
}

interface ScheduleDay {
  day_of_week: number;
  name: string;
  exercises: PlanExercise[];
}

interface PlanData {
  schedule: ScheduleDay[];
  goal: string;
  days_per_week: number;
  equipment: string[];
}

interface Plan {
  id: number;
  name: string;
  description: string | null;
  plan_data: PlanData;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const WEEKDAY_NAMES = ['日', '一', '二', '三', '四', '五', '六'];
const GOAL_OPTIONS = ['增肌', '减脂', '力量', '耐力', '塑形'];
const LEVEL_OPTIONS = ['新手', '中级', '高级'];

export default function PlanView() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // 新增计划表单
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formGoal, setFormGoal] = useState('增肌');
  const [formDays, setFormDays] = useState(4);
  const [formEquipment, setFormEquipment] = useState('');
  const [formLevel, setFormLevel] = useState('中级');
  const [formNotes, setFormNotes] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [generating, setGenerating] = useState(false);

  // SWR: 训练计划（有缓存立即显示，后台刷新替换）
  const { data: plansData, isLoading, error, mutate } = useSWR<Plan[]>(
    ['plans'],
    () => api.getPlans().then(r => r.data.data || [])
  );
  const plans = plansData || [];

  const toggleForm = () => {
    setShowForm(!showForm);
    setFormError('');
    setFormSuccess('');
  };

  const submitPlan = async (e: FormEvent) => {
    e.preventDefault();
    setGenerating(true);
    setFormError('');
    setFormSuccess('');
    try {
      const equipment = formEquipment
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      await api.createPlanByLLM({
        name: formName.trim() || undefined,
        goal: formGoal,
        days_per_week: formDays,
        equipment,
        experience_level: formLevel,
        notes: formNotes.trim() || undefined,
      });
      setFormSuccess('已生成并保存训练计划');
      setShowForm(false);
      setFormName('');
      setFormEquipment('');
      setFormNotes('');
      mutate();
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || '生成失败，请重试');
    } finally {
      setGenerating(false);
    }
  };

  const toggleActive = async (plan: Plan) => {
    try {
      await api.updatePlan(plan.id, { is_active: !plan.is_active });
      mutate();
    } catch (e) {
      console.error(e);
    }
  };

  const deletePlan = async (id: number) => {
    if (!confirm('确定删除这个训练计划？')) return;
    try {
      await api.deletePlan(id);
      mutate();
    } catch (e) {
      console.error(e);
    }
  };

  if (isLoading) return <div className="plan-loading">加载中...</div>;
  if (error) return <div className="plan-error">加载失败</div>;

  return (
    <div className="plan-view">
      <div className="plan-toolbar">
        <button className="plan-add-btn" onClick={toggleForm}>
          {showForm ? '收起' : '＋ 新增计划'}
        </button>
        {formSuccess && <span className="plan-success-tip">✓ {formSuccess}</span>}
      </div>

      {showForm && (
        <form className="plan-form" onSubmit={submitPlan}>
          <div className="plan-form-grid">
            <label className="plan-form-field">
              <span className="plan-form-label">计划名称（可选）</span>
              <input
                type="text"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="如：夏季增肌计划"
              />
            </label>
            <label className="plan-form-field">
              <span className="plan-form-label">目标</span>
              <select value={formGoal} onChange={e => setFormGoal(e.target.value)}>
                {GOAL_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            <label className="plan-form-field">
              <span className="plan-form-label">每周训练天数</span>
              <input
                type="number"
                min={1}
                max={7}
                value={formDays}
                onChange={e => setFormDays(Number(e.target.value))}
              />
            </label>
            <label className="plan-form-field">
              <span className="plan-form-label">器械（逗号分隔）</span>
              <input
                type="text"
                value={formEquipment}
                onChange={e => setFormEquipment(e.target.value)}
                placeholder="如：杠铃,哑铃,龙门架"
              />
            </label>
            <label className="plan-form-field">
              <span className="plan-form-label">经验水平</span>
              <select value={formLevel} onChange={e => setFormLevel(e.target.value)}>
                {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
            <label className="plan-form-field plan-form-field-wide">
              <span className="plan-form-label">备注（可选）</span>
              <textarea
                value={formNotes}
                onChange={e => setFormNotes(e.target.value)}
                placeholder="如：膝盖有旧伤，避免深蹲"
                rows={2}
              />
            </label>
          </div>
          {formError && <div className="plan-form-error">{formError}</div>}
          <div className="plan-form-actions">
            <button type="button" className="plan-form-cancel" onClick={toggleForm}>取消</button>
            <button type="submit" className="plan-form-submit" disabled={generating}>
              {generating ? '生成中…' : '生成计划'}
            </button>
          </div>
        </form>
      )}

      {plans.length === 0 ? (
        <div className="plan-empty">
          <div className="plan-empty-icon">📋</div>
          <h3>还没有训练计划</h3>
          <p>去<strong>对话</strong>页面告诉 AI 你的目标和条件，让它帮你生成一份计划。</p>
          <p className="plan-empty-hint">
            例如：「帮我生成一个4天增肌计划，我有杠铃和哑铃」
          </p>
        </div>
      ) : (
        <div className="plan-list">
          {plans.map(plan => {
            const pd = plan.plan_data;
            const isExpanded = expandedId === plan.id;

            return (
              <div key={plan.id} className={`plan-card ${plan.is_active ? 'active' : ''}`}>
                <div className="plan-card-header" onClick={() => setExpandedId(isExpanded ? null : plan.id)}>
                  <div className="plan-card-left">
                    <span className={`plan-active-badge ${plan.is_active ? 'on' : ''}`}>
                      {plan.is_active ? '● 进行中' : '○ 未激活'}
                    </span>
                    <h3 className="plan-name">{plan.name}</h3>
                    {pd.goal && <span className="plan-goal-tag">{pd.goal}</span>}
                    <span className="plan-meta">{pd.days_per_week}天/周</span>
                    {pd.equipment?.length > 0 && (
                      <span className="plan-equip">{pd.equipment.join('、')}</span>
                    )}
                  </div>
                  <div className="plan-card-right">
                    <button
                      className={`plan-toggle-btn ${plan.is_active ? 'on' : ''}`}
                      onClick={(e) => { e.stopPropagation(); toggleActive(plan); }}
                      title={plan.is_active ? '取消激活' : '激活计划'}
                    >
                      {plan.is_active ? '停用' : '启用'}
                    </button>
                    <button
                      className="plan-delete-btn"
                      onClick={(e) => { e.stopPropagation(); deletePlan(plan.id); }}
                      title="删除"
                    >
                      🗑
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="plan-card-body">
                    {plan.description && (
                      <p className="plan-desc">{plan.description}</p>
                    )}
                    <div className="plan-schedule">
                      {pd.schedule?.map((day, i) => (
                        <div key={i} className="plan-day">
                          <div className="plan-day-header">
                            <span className="plan-day-name">
                              {day.day_of_week === 0 ? '周日' : `周${WEEKDAY_NAMES[day.day_of_week]}`} — {day.name}
                            </span>
                          </div>
                          <div className="plan-exercises">
                            {day.exercises?.map((ex, j) => (
                              <div key={j} className="plan-exercise-row">
                                <span className="plan-ex-name">{ex.name}</span>
                                <span className="plan-ex-sets">{ex.sets}组</span>
                                <span className="plan-ex-range">{ex.reps_range}</span>
                                {ex.notes && <span className="plan-ex-notes">{ex.notes}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="plan-footer-meta">
                      <span>创建于 {new Date(plan.created_at).toLocaleDateString('zh-CN')}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
